/**
 * Editor UI slice - manages UI state (menu visibility, etc.)
 */

/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  isMenuVisible: false,
  menuPosition: { left: 0, bottom: 0 },
  isScrubbing: false,
  scrubTime: 0,
  isPreviewMode: false,
  isAssignSectionModalVisible: false,
  isQuestionModalVisible: false,
  isTextModalVisible: false,
  isPreviewModalVisible: false,
  isPublishMeetingModalVisible: false,
  isPublishProgressModalVisible: false,
  isUpdateMeetingTitleModalVisible: false,
  isAssignSectionErrorPopupVisible: false,
  isAssignSectionSuccessPopupVisible: false,
  isSaveSuccessModalVisible: false,
  isContributorClosedModalVisible: false,
  isCloseSectionSuccessModalVisible: false,
  isNotificationManagerVisible: false,
  isSaveBeforePublishModalVisible: false,
  isSavingBeforePublish: false, // Track if we're in save-before-publish flow
  isSelectShareOptionModalVisible: false,
  isShareMeetingModalVisible: false,
  isRequestReviewModalVisible: false,
  isPermissionManagerVisible: false,
  permissionManagerResource: null,
  // Viewer modal states
  isViewImageSectionModalVisible: false,
  isViewUploadDocumentsModalVisible: false,
  // Modal lifecycle tracking for viewer
  visibleModalSectionId: null, // Which section's modal is currently visible
  handledSectionIds: [], // Array of section IDs that have been handled (shown and closed/answered)
  // Editor context states
  editingQuestionContext: null, // { assetId, sectionId? }
  editingTextContext: null, // { assetId, sectionId?, text }
  editingPlaceholderContext: null, // { assetId, sectionId?, itemId, title, description, metadata? }
  fetchedQuestionData: null,
  editModalItemId: null,
  trimModalData: null, // { itemId, asset, itemDuration, existingTrimStart, existingTrimEnd }
  pendingTrimData: null, // { itemId, trimStart, trimEnd } - unsaved trim values during editing
  focusedSectionTitleId: null,
  hoveredSectionTitleId: null,
  draggedItemId: null,
  dragOverIndex: null,
  isFullscreen: false,
  hasUnsavedChanges: false,
  lastSavedHistoryLength: 0,
  pendingNavigation: null, // { action, location } from unstable_useBlocker
  isLeaveConfirmationVisible: false,
  isPlaceholderModalVisible: false,
};

const editorUISlice = createSlice({
  name: 'editorUI',
  initialState,
  reducers: {
    setMenuVisible: (state, action) => {
      state.isMenuVisible = action.payload;
    },
    setPermissionManagerVisible: (state, action) => {
      state.isPermissionManagerVisible = action.payload;
    },
    setPermissionManagerResource: (state, action) => {
      state.permissionManagerResource = action.payload;
    },
    setMenuPosition: (state, action) => {
      state.menuPosition = action.payload;
    },
    startScrub: (state, action) => {
      state.isScrubbing = true;
      state.scrubTime = action.payload;
    },
    updateScrub: (state, action) => {
      if (state.isScrubbing) {
        state.scrubTime = action.payload;
      }
    },
    endScrub: (state) => {
      state.isScrubbing = false;
    },
    setPreviewMode: (state, action) => {
      state.isPreviewMode = !!action.payload;
    },
    togglePreviewMode: (state) => {
      state.isPreviewMode = !state.isPreviewMode;
    },
    setAssignSectionModalVisible: (state, action) => {
      state.isAssignSectionModalVisible = action.payload;
    },
    setQuestionModalVisible: (state, action) => {
      state.isQuestionModalVisible = action.payload;
    },
    setTextModalVisible: (state, action) => {
      state.isTextModalVisible = action.payload;
    },
    setPreviewModalVisible: (state, action) => {
      state.isPreviewModalVisible = action.payload;
    },
    setPublishMeetingModalVisible: (state, action) => {
      state.isPublishMeetingModalVisible = action.payload;
    },
    setPublishProgressModalVisible: (state, action) => {
      state.isPublishProgressModalVisible = action.payload;
    },
    setUpdateMeetingTitleModalVisible: (state, action) => {
      state.isUpdateMeetingTitleModalVisible = action.payload;
    },
    setAssignSectionErrorPopupVisible: (state, action) => {
      state.isAssignSectionErrorPopupVisible = action.payload;
    },
    setAssignSectionSuccessPopupVisible: (state, action) => {
      state.isAssignSectionSuccessPopupVisible = action.payload;
    },
    setSaveSuccessModalVisible: (state, action) => {
      state.isSaveSuccessModalVisible = action.payload;
    },
    setContributorClosedModalVisible: (state, action) => {
      state.isContributorClosedModalVisible = action.payload;
    },
    setCloseSectionSuccessModalVisible: (state, action) => {
      state.isCloseSectionSuccessModalVisible = action.payload;
    },
    setNotificationManagerVisible: (state, action) => {
      state.isNotificationManagerVisible = action.payload;
    },
    setSaveBeforePublishModalVisible: (state, action) => {
      state.isSaveBeforePublishModalVisible = action.payload;
    },
    setSelectShareOptionModalVisible: (state, action) => {
      state.isSelectShareOptionModalVisible = action.payload;
    },
    setShareMeetingModalVisible: (state, action) => {
      state.isShareMeetingModalVisible = action.payload;
    },
    setRequestReviewModalVisible: (state, action) => {
      state.isRequestReviewModalVisible = action.payload;
    },
    setIsSavingBeforePublish: (state, action) => {
      state.isSavingBeforePublish = action.payload;
    },
    setViewImageSectionModalVisible: (state, action) => {
      state.isViewImageSectionModalVisible = action.payload;
    },
    setViewUploadDocumentsModalVisible: (state, action) => {
      state.isViewUploadDocumentsModalVisible = action.payload;
    },
    // Modal lifecycle tracking for viewer
    setVisibleModalSectionId: (state, action) => {
      state.visibleModalSectionId = action.payload;
    },
    markSectionAsHandled: (state, action) => {
      const sectionId = action.payload;
      if (!state.handledSectionIds.includes(sectionId)) {
        state.handledSectionIds.push(sectionId);
      }
    },
    clearHandledSection: (state, action) => {
      const sectionId = action.payload;
      state.handledSectionIds = state.handledSectionIds.filter((id) => id !== sectionId);
    },
    clearAllHandledSections: (state) => {
      state.handledSectionIds = [];
    },
    // Editor context reducers
    setEditingQuestionContext: (state, action) => {
      state.editingQuestionContext = action.payload;
    },
    setEditingTextContext: (state, action) => {
      state.editingTextContext = action.payload;
    },
    setEditingPlaceholderContext: (state, action) => {
      state.editingPlaceholderContext = action.payload;
    },
    setFetchedQuestionData: (state, action) => {
      state.fetchedQuestionData = action.payload;
    },
    setEditModalItemId: (state, action) => {
      state.editModalItemId = action.payload;
    },
    setTrimModalData: (state, action) => {
      state.trimModalData = action.payload;
      // Clear pending trim when modal closes
      if (!action.payload) {
        state.pendingTrimData = null;
      }
    },
    setPendingTrim: (state, action) => {
      state.pendingTrimData = action.payload;
    },
    clearPendingTrim: (state) => {
      state.pendingTrimData = null;
    },
    setFocusedSectionTitleId: (state, action) => {
      state.focusedSectionTitleId = action.payload;
    },
    setHoveredSectionTitleId: (state, action) => {
      state.hoveredSectionTitleId = action.payload;
    },
    setDraggedItemId: (state, action) => {
      state.draggedItemId = action.payload;
    },
    setDragOverIndex: (state, action) => {
      state.dragOverIndex = action.payload;
    },
    setIsFullscreen: (state, action) => {
      state.isFullscreen = !!action.payload;
    },
    setHasUnsavedChanges: (state, action) => {
      state.hasUnsavedChanges = !!action.payload;
    },
    setLastSavedHistoryLength: (state, action) => {
      state.lastSavedHistoryLength = typeof action.payload === 'number' ? action.payload : 0;
    },
    setPendingNavigation: (state, action) => {
      state.pendingNavigation = action.payload;
    },
    setLeaveConfirmationVisible: (state, action) => {
      state.isLeaveConfirmationVisible = !!action.payload;
    },
    setPlaceholderModalVisible: (state, action) => {
      state.isPlaceholderModalVisible = !!action.payload;
    },
    resetEditorUI: (state) => {
      Object.assign(state, initialState);
    },
  },
});

export const {
  setMenuVisible,
  setMenuPosition,
  startScrub,
  updateScrub,
  endScrub,
  setPreviewMode,
  togglePreviewMode,
  setAssignSectionModalVisible,
  setQuestionModalVisible,
  setTextModalVisible,
  setPreviewModalVisible,
  setPublishMeetingModalVisible,
  setPublishProgressModalVisible,
  setUpdateMeetingTitleModalVisible,
  setAssignSectionErrorPopupVisible,
  setAssignSectionSuccessPopupVisible,
  setSaveSuccessModalVisible,
  setContributorClosedModalVisible,
  setCloseSectionSuccessModalVisible,
  setNotificationManagerVisible,
  setSaveBeforePublishModalVisible,
  setSelectShareOptionModalVisible,
  setShareMeetingModalVisible,
  setRequestReviewModalVisible,
  setPermissionManagerVisible,
  setPermissionManagerResource,
  setIsSavingBeforePublish,
  setViewImageSectionModalVisible,
  setViewUploadDocumentsModalVisible,
  setVisibleModalSectionId,
  markSectionAsHandled,
  clearHandledSection,
  clearAllHandledSections,
  setEditingQuestionContext,
  setEditingTextContext,
  setEditingPlaceholderContext,
  setFetchedQuestionData,
  setEditModalItemId,
  setTrimModalData,
  setPendingTrim,
  clearPendingTrim,
  setFocusedSectionTitleId,
  setHoveredSectionTitleId,
  setDraggedItemId,
  setDragOverIndex,
  setIsFullscreen,
  setHasUnsavedChanges,
  setLastSavedHistoryLength,
  setPendingNavigation,
  setLeaveConfirmationVisible,
  setPlaceholderModalVisible,
  resetEditorUI,
} = editorUISlice.actions;

export const selectHasUnsavedChanges = (state) => state.editorUI.hasUnsavedChanges;
export const selectPendingNavigation = (state) => state.editorUI.pendingNavigation;
export const selectIsLeaveConfirmationVisible = (state) => state.editorUI.isLeaveConfirmationVisible;
export const selectLastSavedHistoryLength = (state) => state.editorUI.lastSavedHistoryLength;

export default editorUISlice.reducer;
